import {
    AlertTriangle,
    ArrowRight,
    CheckCircle,
    ClipboardList,
    Clock,
    Cpu,
    CreditCard,
    Fingerprint,
    Headphones,
    LayoutDashboard,
    Mail,
    MapPin,
    Phone,
    Shield,
    TrendingUp,
    Video,
    Wifi
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/header/Navbar';
import { useAuth } from '../hooks/useAuth';
import { useComplaintsCount } from '../hooks/useComplaintsCount';

const Dashboard = () => {
    const { user } = useAuth();
    const counts = useComplaintsCount();

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
            <Navbar user={user} />

            {/* Complaint Categories */}
            <div className="max-w-6xl mx-auto px-6 py-16">
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
                        Select a <span className="text-blue-600">Category</span>
                    </h2>
                    <p className="text-gray-500 mt-2 text-lg">Choose the type of issue you'd like to manage</p>
                </div>

                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">

                    <Link to={"/attendance"} className={`group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 flex flex-col items-center p-2 lg:p-4 text-center cursor-pointer no-underline`}>
                        <div className={`w-20 h-20 rounded-full bg-green-100 flex items-center justify-center transition-colors duration-300`}>
                            <Fingerprint className='w-10 h-10 text-green-500' />
                        </div>
                        <span className={`mt-6 text-2xl font-bold text-gray-800 group-hover:text-gray-700 transition-colors`}>Attendance</span>
                        <p className={`mt-2 text-gray-600 text-sm leading-relaxed hidden lg:block`}>
                            Please navigate here for let company know that you are attend to your job
                        </p>
                        <div className={`mt-4 opacity-0 group-hover:opacity-100 transition-opacity hidden lg:block`}>
                            <ArrowRight className="w-5 h-5" />
                        </div>
                    </Link>

                    {[
                        { name: 'Internet', path: '/complaints/Internet', color: 'blue', icon: <Wifi className="w-10 h-10" /> },
                        { name: 'CCTV', path: '/complaints/CCTV', color: 'indigo', icon: <Video className="w-10 h-10" /> },
                        { name: 'Billing', path: '/complaints/Billing', color: 'emerald', icon: <CreditCard className="w-10 h-10" /> },
                        { name: 'Hardware', path: '/complaints/Hardware', color: 'purple', icon: <Cpu className="w-10 h-10" /> },
                        { name: 'Access Control', path: '/complaints/Access%20Control', color: 'rose', icon: <Shield className="w-10 h-10" /> }
                    ].map((category) => (
                        <Link
                            key={category.name}
                            to={category.path}
                            className={`group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-${category.color}-200 flex flex-col items-center p-2 lg:p-4 text-center cursor-pointer no-underline`}
                        >
                            <div className={`w-20 h-20 bg-${category.color}-50 rounded-full flex items-center justify-center text-${category.color}-600 group-hover:bg-${category.color}-100 transition-colors duration-300`}>
                                {category.icon}
                            </div>
                            <h3 className={`mt-6 text-2xl font-bold text-gray-800 group-hover:text-${category.color}-600 transition-colors`}>
                                {category.name}
                            </h3>
                            <p className={`mt-2 text-gray-600 text-sm leading-relaxed hidden lg:block`}>
                                {category.name === 'Internet' && 'Report internet, Wi-Fi connectivity, or hardware issues related to routers.'}
                                {category.name === 'CCTV' && 'Report camera feeds, offline channels, or DVR/NVR recording failures.'}
                                {category.name === 'Billing' && 'Report billing discrepancies, service interruptions, or payment issues.'}
                                {category.name === 'Hardware' && 'Report issues with hardware components, devices, or infrastructure.'}
                                {category.name === 'Access Control' && 'Report issues with biometric systems, card readers, and door access.'}
                            </p>
                            <div className={`mt-4 text-${category.color}-500 opacity-0 group-hover:opacity-100 transition-opacity hidden lg:block`}>
                                <ArrowRight className="w-5 h-5" />
                            </div>
                        </Link>
                    ))}
                </div>
            </div>

            {/* Quick Stats */}
            <div className="max-w-6xl mx-auto px-6 mt-8 relative z-10">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                        { label: 'Total Complaints', value: counts.total || 0, icon: <TrendingUp className="w-6 h-6 text-blue-600" />, color: 'blue' },
                        { label: 'Resolved', value: counts.completed || 0, icon: <CheckCircle className="w-6 h-6 text-green-600" />, color: 'green' },
                        { label: 'Pending', value: counts.pending || 0, icon: <Clock className="w-6 h-6 text-yellow-600" />, color: 'yellow' },
                        { label: 'Urgent', value: counts.urgent || 0, icon: <AlertTriangle className="w-6 h-6 text-red-600" />, color: 'red' },
                    ].map((stat, idx) => (
                        <div
                            key={idx}
                            className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-5 flex items-center gap-4 border border-white/50 transition-all hover:shadow-xl hover:scale-105"
                        >
                            <div className={`p-3 rounded-xl bg-${stat.color}-50`}>
                                {stat.icon}
                            </div>
                            <div>
                                <p className="text-sm text-gray-500 font-medium">{stat.label}</p>
                                <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {/* Footer */}
            <footer className="bg-gray-800 text-gray-300 py-10 mt-12">
                <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div>
                        <h4 className="text-white text-xl font-bold mb-4">Complaint Manager</h4>
                        <p className="text-sm leading-relaxed">
                            Efficiently track and resolve complaints across all departments.
                        </p>
                    </div>
                    <div>
                        <h5 className="text-white font-semibold mb-3">Quick Links</h5>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <Link to="/" className="hover:text-white transition-colors flex items-center gap-2">
                                    <LayoutDashboard className="w-4 h-4" />
                                    Dashboard
                                </Link>
                            </li>
                            <li>
                                <Link to="/complaints" className="hover:text-white transition-colors flex items-center gap-2">
                                    <ClipboardList className="w-4 h-4" />
                                    Complaints
                                </Link>
                            </li>
                            <li>
                                <Link to="/support" className="hover:text-white transition-colors flex items-center gap-2">
                                    <Headphones className="w-4 h-4" />
                                    Support
                                </Link>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <h5 className="text-white font-semibold mb-3">Contact</h5>
                        <ul className="space-y-2 text-sm">
                            <li className="flex items-center gap-2">
                                <Mail className="w-4 h-4" />
                                support@bijoyit.com
                            </li>
                            <li className="flex items-center gap-2">
                                <Phone className="w-4 h-4" />
                                +88 0000000000
                            </li>
                            <li className="flex items-center gap-2">
                                <MapPin className="w-4 h-4" />
                                Gouripur, mymensingh, Bangladesh
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-gray-700 mt-8 pt-6 text-center text-sm">
                    &copy; {new Date().getFullYear()} Complaint Management System. All rights reserved.
                </div>
            </footer>
        </div>
    );
};

export default Dashboard;